import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useTypedSelector } from "../../../store/reducers/use-typed-selector";
import { BLAdmin, Channel } from "../../../types/working-model";
import Icon, { COLORS, ICONS } from "../../general-ui/icon";
import WaitingIndicator from "../../general-ui/waiting-indicator/waiting-indicator";
import { UploadFile } from "../../../connection/uploads";
import EditChannelModal from "./edit-channel";
import { updateChannel } from "../../../store/actions/authentication";

interface SettingsChannelProps {
	openModal: boolean;
	closeModal: () => void;
}

export default function SettingsChannel({ openModal, closeModal }: SettingsChannelProps): JSX.Element {
	const { user, token, channels } = useTypedSelector((state) => state.AuthReducer);
	const { admins } = useTypedSelector((state) => state.ChannelAdminReducer);

	const userPermission = useMemo(() => {
		return user?.channels[user?.active_channel].some(permission => permission === "owner" || permission === "admin");
	}, [user]);

	const currentChannel: Channel | undefined = useMemo(() => {
		return channels.find((channel) => channel.channel === user?.active_channel);
	}, [user, channels]);

	const channelImage: string = useMemo(() => {
		const url =
			currentChannel?.logos?.color ||
			currentChannel?.logos?.white ||
			currentChannel?.logos?.gray ||
			"";
		return url;
	}, [currentChannel]);

	const primaryContact: BLAdmin | undefined = useMemo(() => {
		return admins.find(
			(admin) => admin.id === currentChannel?.primary_contact
		);
	}, [admins, currentChannel]);

	const contactName: string = useMemo(() => {
		return primaryContact?.profile?.first_name || primaryContact?.profile?.last_name ?
			`${primaryContact?.profile?.first_name} ${primaryContact?.profile?.last_name}` : "none listed";
	}, [primaryContact]);

	const dispatch = useDispatch();

	const [imageUploading, setImageUploading] = useState<boolean>(false);

	const handleNewImage = async (imageFile: FileList | null) => {
		if (imageFile) {
			setImageUploading(true);
			const uploaded = await UploadFile(user!, token!, imageFile[0]);
			const channelEdit = { ...currentChannel, logos: { ...currentChannel?.logos, color: uploaded } };
			dispatch(updateChannel(token!, channelEdit as Channel));
			setImageUploading(false);
		}
	};

	return (
		<div className="settings-channel-container">
			{currentChannel ? (
				<>
					<div className="avatar">
						{imageUploading ? (
							<WaitingIndicator />
						) : (<>
								{channelImage ?
									<img src={channelImage} alt="channel image" /> : <p>no image</p>}
								{userPermission && (<>
									<input
										type={"file"}
										name={"imageUpload"}
										id={"imageUpload"}
										accept={"image/*"}
										multiple={false}
										style={{ display: "none" }}
										disabled={!userPermission}
										onChange={(event) => handleNewImage(event?.target?.files)}
									/>
									<label htmlFor="imageUpload">
										<div className="camera-button">
											<Icon name={ICONS.PHOTO} color={COLORS.BLACK} size={20} />
										</div>
									</label>
							</>)}
						</>)}
					</div>
					<div>
						<h3>{currentChannel.name}</h3>
						<div className="primary-info">
							<div className="primary-info-icon">
								<Icon name={ICONS.PERSON} color={COLORS.WHITE} size={16} />
							</div>
							<div className="primary-info-text">
								<span className="s3">Primary contact name</span>
								<p className="p1">{contactName}</p>
							</div>
						</div>
						<div className="primary-info">
							<div className="primary-info-icon">
								<Icon name={ICONS.EMAIL} color={COLORS.WHITE} size={16} />
							</div>
							<div className="primary-info-text">
								<span className="s3">Primary contact email</span>
								<p className="p1">{primaryContact?.email || "none listed"}</p>
							</div>
						</div>
						<div className="primary-info">
							<div className="primary-info-icon">
								<Icon name={ICONS.URL} color={COLORS.WHITE} size={16} />
							</div>
							<div className="primary-info-text">
								<span className="s3">URL used for events</span>
								<p className="p1">{currentChannel.url}</p>
							</div>
						</div>
					</div>
					<EditChannelModal isOpen={openModal} onClose={closeModal} />
				</>
			) : (
				<p>no channel set</p>
			)}
		</div>
	);
}
